/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { Box, Divider, Link, Stack, SxProps, Typography } from '@mui/material';
import { isNil } from 'lodash';
import React, { useState } from 'react';
import FlightLegServiceAlerts from '../../../shared/entities/flightLegServiceAlerts';
import MissionPlanDetails from '../../../shared/entities/missionPlanDetails';
import { minutesToFormattedMinutesString } from '../../../utils/DateTimeUtils';
import { EMPTY_FIELD_STRING_PLACEHOLDER } from '../../../utils/StringUtils';
import appTheme from '../../common/theme/appTheme.json';
import { ContourDisplayEnum } from './PerformanceOutlookExportModal';
import ServiceTiersSlaChart from '../../common/table/ServiceTiersSlaChart';

interface PerformanceOutlookLegInfoProps {
  missionPlan: MissionPlanDetails;
  serviceAlerts?: { [flightLegId: number]: FlightLegServiceAlerts };
  flightLegId?: number;
  contourDisplayType?: ContourDisplayEnum;
  serviceTiersSlaChartEnabled?: boolean;
  sx?: SxProps;
}

const PerformanceOutlookLegInfo = ({
  missionPlan,
  serviceAlerts,
  flightLegId,
  contourDisplayType,
  serviceTiersSlaChartEnabled,
  sx,
}: PerformanceOutlookLegInfoProps) => {
  const [displaySlaChart, setDisplaySlaChart] = useState(false);
  return (
    <React.Fragment>
      {!isNil(serviceAlerts) && !isNil(flightLegId) && !isNil(missionPlan.name) && (
        <Box
          sx={{
            color: appTheme.primaryTextColor,
            paddingLeft: '24px',
            paddingRight: '24px',
            boxSizing: 'border-box',
            display: 'flex',
            justifyContent: 'space-between',
            [`@media print`]: {
              width: '100%',
              color: 'black',
              padding: 0,
            },
            ...sx,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: 'column',
            }}
          >
            <Box
              data-test="mission-name"
              sx={{
                fontSize: '24px',
                fontWeight: 800,
                lineHeight: '32px',
                '@media print': { fontSize: '16px', lineHeight: '24px' },
              }}
            >
              {missionPlan.name}
            </Box>
            <Stack
              direction="row"
              divider={<Divider orientation="vertical" sx={{ backgroundColor: 'white' }} flexItem />}
              spacing={1}
              sx={{
                display: 'flex',
                alignItems: 'center',
                marginTop: '10px',
                fontSize: '14px',
                '@media print': { marginTop: 0 },
              }}
            >
              <Box data-test="flight-leg-distance">
                {!isNil(serviceAlerts[flightLegId]?.distanceNmi)
                  ? `${serviceAlerts[flightLegId]?.distanceNmi}nm`
                  : `${EMPTY_FIELD_STRING_PLACEHOLDER}`}
              </Box>
              <Box data-test="flight-leg-duration">
                {!isNil(serviceAlerts[flightLegId]?.durationMin)
                  ? `${minutesToFormattedMinutesString(serviceAlerts[flightLegId]?.durationMin)}`
                  : `${EMPTY_FIELD_STRING_PLACEHOLDER}`}
              </Box>
              <Box data-test="mission-plan-aircraft">
                {!isNil(missionPlan.aircraftTailNum)
                  ? missionPlan.aircraftTailNum
                  : `${EMPTY_FIELD_STRING_PLACEHOLDER}`}
              </Box>
              {missionPlan.useHcxBeams && (
                <Box
                  data-test="mission-hcx-enabled"
                  sx={{
                    width: '82px',
                    height: '20px',
                    fontSize: '12px',
                    color: appTheme.mptSecondaryBlue,
                    border: `1px solid ${appTheme.mptSecondaryBlue}`,
                    borderRadius: '5px',
                    backgroundColor: `${appTheme.mptSecondaryBlue}26`, // blue with 15% (26 in hex) opacity
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  HCX Enabled
                </Box>
              )}
            </Stack>
          </Box>
          <Box
            sx={{
              width: '50%',
              display: 'block',
              textAlign: 'right',
            }}
          >
            <Box
              sx={{
                color: appTheme.primaryTextColor,
                fontSize: '14px',
                lineHeight: '28px',
                display: 'flex',
                justifyContent: 'flex-end',
                fontWeight: 500,
                '@media print': {
                  color: 'black',
                },
              }}
              data-test="sed-sscf-version"
            >
              {serviceTiersSlaChartEnabled && (
                <Typography
                  sx={{ fontSize: 'inherit', lineHeight: 'inherit', fontWeight: 'inherit', color: 'inherit' }}
                >
                  <Link
                    color="#009DE0"
                    underline="hover"
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      setDisplaySlaChart(true);
                    }}
                  >
                    Service Tiers
                  </Link>
                  &nbsp;|&nbsp;
                </Typography>
              )}
              Map Beam Version: {missionPlan?.sscfFileVersion} / {missionPlan?.sedFileVersion}{' '}
              {!missionPlan?.useNmsConfiguredMapVersion ? '(local)' : ''}
            </Box>
            <Box
              sx={{
                color: appTheme.primaryTextColor,
                fontWeight: 700,
                fontSize: '14px',
                '@media print': {
                  color: 'black',
                },
              }}
            >
              {contourDisplayType === ContourDisplayEnum.FL
                ? 'Forward Link Contours Displayed'
                : contourDisplayType === ContourDisplayEnum.RL && 'Return Link Contours Displayed'}
            </Box>
          </Box>
        </Box>
      )}
      <ServiceTiersSlaChart
        open={displaySlaChart}
        onClose={() => {
          setDisplaySlaChart(false);
        }}
      />
    </React.Fragment>
  );
};

export default PerformanceOutlookLegInfo;
